import { cn } from '@/utils';

interface YesNoToggleProps {
  label: string;
  value: 'yes' | 'no' | undefined;
  onChange: (value: 'yes' | 'no') => void;
  error?: string;
  required?: boolean;
}

/**
 * A labelled Yes/No pill pair — same visual language as the Life branch's
 * YesNoToggle (steps/life/YesNoToggle.tsx). Kept as a separate copy here
 * (rather than importing across branches) so Health stays self-contained,
 * matching how Motor and Life each own their full set of step components.
 */
export function YesNoToggle({ label, value, onChange, error, required }: YesNoToggleProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="flex gap-3">
        {(['yes', 'no'] as const).map((option) => (
          <label
            key={option}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors',
              value === option
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            )}
          >
            <input
              type="radio"
              checked={value === option}
              onChange={() => onChange(option)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
            />
            <span className="text-sm text-gray-700 dark:text-gray-200">
              {option === 'yes' ? 'Yes' : 'No'}
            </span>
          </label>
        ))}
      </div>
      {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
